import React, { useReducer, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { COUNTRIES } from '../../../constants/countries';

// Consolidates the related autocomplete state (input string + dropdown-open +
// active-index) into a single reducer so a keystroke/selection updates them
// atomically and the position-aligned active index stays in lockstep with the
// rendered match list. One instance per dropdown (preferred / avoid).
const initialState = { input: '', showDropdown: false, activeIndex: -1 };

function reducer(state, action) {
    switch (action.type) {
        case 'TYPE':
            // typing always re-opens the dropdown (preserves prior onChange semantics)
            return { ...state, input: action.value, showDropdown: true };
        case 'OPEN':
            return { ...state, showDropdown: true };
        case 'CLOSE':
            return { ...state, showDropdown: false };
        case 'SET_ACTIVE':
            return { ...state, activeIndex: action.index };
        case 'RESET':
            return { input: '', showDropdown: false, activeIndex: -1 };
        default:
            return state;
    }
}

export default function LocationDropdown({
    id,
    label,
    placeholder = 'Search country...',
    type,
    selected,
    activeClassName,
    tagClassName,
    tagButtonClassName,
    tagRemoveAriaPrefix,
    onAdd,
    onRemove,
}) {
    const [state, dispatch] = useReducer(reducer, initialState);
    const { input, showDropdown, activeIndex } = state;
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                dispatch({ type: 'CLOSE' });
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const addLocation = (country) => {
        onAdd(type, country);
        dispatch({ type: 'RESET' });
    };

    const handleKeyDown = (e) => {
        const matches = COUNTRIES.filter(c => c.toLowerCase().includes(input.toLowerCase()));

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            dispatch({ type: 'SET_ACTIVE', index: activeIndex < matches.length - 1 ? activeIndex + 1 : activeIndex });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            dispatch({ type: 'SET_ACTIVE', index: activeIndex > 0 ? activeIndex - 1 : activeIndex });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && matches[activeIndex]) {
                addLocation(matches[activeIndex]);
            } else if (input.trim() && matches.length > 0) {
                // Fallback to first match if no active selection but input exists (optional, or stick to strict selection)
                addLocation(matches[0]);
            } else {
                dispatch({ type: 'RESET' });
            }
        }
    };

    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-gray-900 mb-1">
                {label}
            </label>
            <div className="relative" ref={containerRef}>
                <input
                    id={id}
                    type="text"
                    placeholder={placeholder}
                    value={input}
                    onChange={(e) => dispatch({ type: 'TYPE', value: e.target.value })}
                    onFocus={() => dispatch({ type: 'OPEN' })}
                    onKeyDown={handleKeyDown}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 hover:border-blue-300 transition-colors"
                />
                {showDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {(() => { let idx = -1; return COUNTRIES.flatMap((country) => {
                            if (!country.toLowerCase().includes(input.toLowerCase())) return [];
                            idx += 1;
                            const i = idx;
                            return [(
                                <button
                                    type="button"
                                    key={country}
                                    className={`block w-full text-left px-3 py-2 cursor-pointer text-sm ${i === activeIndex ? activeClassName : 'hover:bg-gray-50'}`}
                                    onClick={() => addLocation(country)}
                                >
                                    {country}
                                </button>
                            )];
                        }); })()}
                    </div>
                )}
            </div>
            {/* Selected Tags */}
            {(selected || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {(selected || []).map((loc) => (
                        <span key={loc} className={tagClassName}>
                            {loc}
                            <button type="button" onClick={() => onRemove(type, loc)} className={tagButtonClassName} aria-label={`${tagRemoveAriaPrefix} ${loc}`}>
                                <X size={12} />
                            </button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}
